import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import Loader from '../../../components/common/Loader';
import { assignManager, getReporteeCourseProgress, getReportingUsers, getSubtreeCourseSummary } from '../../../services/adminService';
import { usePermissions } from '../../../context/PermissionsContext';

function Reporting({ embedded = false, onClose }) {
  const { permissions } = usePermissions();

  const [activeTab, setActiveTab] = useState('assignment');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryRows, setSummaryRows] = useState([]);
  const [summaryQuery, setSummaryQuery] = useState('');

  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [courseModalLoading, setCourseModalLoading] = useState(false);
  const [courseModalUser, setCourseModalUser] = useState(null);
  const [courseModalCourses, setCourseModalCourses] = useState([]);

  const [reporteeId, setReporteeId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [lockUser, setLockUser] = useState(false);

  const managerOptions = useMemo(() => {
    const rId = reporteeId ? Number(reporteeId) : null;
    return users.filter((u) => Number(u.id) !== rId);
  }, [users, reporteeId]);

  const selectedReportee = useMemo(
    () => users.find((u) => String(u.id) === String(reporteeId)),
    [users, reporteeId]
  );

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res = await getReportingUsers();
        if (res?.success) {
          const list = Array.isArray(res?.data?.users) ? res.data.users : Array.isArray(res?.data) ? res.data : [];
          setUsers(list);
        } else {
          toast.error(res?.message || res?.error_msg || 'Failed to load users');
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    if (embedded) return;
    if (activeTab !== 'progress') return;

    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        const res = await getSubtreeCourseSummary();
        if (res?.success) {
          const rows = Array.isArray(res?.data?.rows)
            ? res.data.rows
            : Array.isArray(res?.data)
              ? res.data
              : [];
          setSummaryRows(rows);
        } else {
          toast.error(res?.message || res?.error_msg || 'Failed to load summary');
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || 'Failed to load summary');
      } finally {
        setSummaryLoading(false);
      }
    };

    fetchSummary();
  }, [activeTab, embedded]);

  const filteredSummaryRows = useMemo(() => {
    const q = (summaryQuery || '').trim().toLowerCase();
    return (summaryRows || []).filter((r) => {
      const name = (r?.user_name || r?.name || '').toString().toLowerCase();
      const email = (r?.user_email || r?.email || '').toString().toLowerCase();
      return !q || name.includes(q) || email.includes(q);
    });
  }, [summaryRows, summaryQuery]);

  const renderStatus = (value) => {
    const v = (value || '').toString().toLowerCase();
    if (v === 'completed') {
      return <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-black">Completed</span>;
    }
    if (v === 'in_progress' || v === 'in progress' || v === 'inprogress') {
      return <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-black">In Progress</span>;
    }
    if (v === 'not_started' || v === 'not started' || v === 'notstarted') {
      return <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-black">Not Started</span>;
    }
    return <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-black">-</span>;
  };

  const openReporteeCourses = async (row) => {
    const userId = row?.user_id ?? row?.id;
    if (!userId) return;
    setCourseModalOpen(true);
    setCourseModalLoading(true);
    setCourseModalUser({ user_id: userId, user_name: row?.user_name ?? row?.name ?? '', user_email: row?.user_email ?? row?.email ?? '' });
    setCourseModalCourses([]);
    try {
      const res = await getReporteeCourseProgress({ user_id: userId });
      if (res?.success) {
        const list = Array.isArray(res?.data?.courses) ? res.data.courses : [];
        const user = res?.data?.user;
        if (user) {
          setCourseModalUser(user);
        }
        setCourseModalCourses(list);
      } else {
        toast.error(res?.message || res?.error_msg || 'Failed to load courses');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to load courses');
    } finally {
      setCourseModalLoading(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      if (detail?.userId != null) {
        setReporteeId(String(detail.userId));
      }
      if (Object.prototype.hasOwnProperty.call(detail, 'managerId')) {
        setManagerId(detail.managerId == null ? '' : String(detail.managerId));
      }
      if (Object.prototype.hasOwnProperty.call(detail, 'lockUser')) {
        setLockUser(Boolean(detail.lockUser));
      } else if (detail?.userId != null) {
        setLockUser(true);
      }
    };

    window.addEventListener('reporting:selectReportee', handler);
    return () => window.removeEventListener('reporting:selectReportee', handler);
  }, []);

  useEffect(() => {
    if (!selectedReportee) return;
    if (selectedReportee.supervisor_id == null) {
      setManagerId('');
      return;
    }
    setManagerId(String(selectedReportee.supervisor_id));
  }, [selectedReportee]);

  const handleSave = async () => {
    if (!permissions.roles_and_access_user_edit) {
      toast.warning("You don't have permission to assign managers");
      return;
    }

    if (!reporteeId) {
      toast.warning('Please select a reportee');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        reportee_id: Number(reporteeId),
        supervisor_id: managerId ? Number(managerId) : null,
      };

      const res = await assignManager(payload);
      if (res?.success) {
        toast.success(res?.message || 'Manager assigned successfully');
        const updated = users.map((u) =>
          String(u.id) === String(reporteeId) ? { ...u, supervisor_id: payload.supervisor_id } : u
        );
        setUsers(updated);
        window.dispatchEvent(
          new CustomEvent('reporting:changed', {
            detail: { reportee_id: payload.reportee_id, supervisor_id: payload.supervisor_id },
          })
        );
      } else {
        toast.error(res?.message || res?.error_msg || 'Failed to assign manager');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to assign manager');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={embedded ? 'p-0' : 'bg-white rounded-[24px] border border-gray-100 shadow-sm p-6'}>
      {!embedded && (
        <div className="flex items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-black text-gray-900">Reporting</h2>
            <p className="text-sm text-gray-500 font-medium">Assign a direct manager (supervisor) to a user.</p>
          </div>
        </div>
      )}

      {!embedded && (
        <div className="flex items-center gap-2 mb-5">
          <button
            type="button"
            onClick={() => setActiveTab('assignment')}
            className={`px-4 py-2 rounded-2xl text-sm font-black border transition-colors ${
              activeTab === 'assignment'
                ? 'bg-indigo-600 border-indigo-600 text-white'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Manager Assignment
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('progress')}
            className={`px-4 py-2 rounded-2xl text-sm font-black border transition-colors ${
              activeTab === 'progress'
                ? 'bg-indigo-600 border-indigo-600 text-white'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Course Progress
          </button>
        </div>
      )}

      {activeTab === 'assignment' && (loading ? (
        <div className="py-16 flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <div className={embedded ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 gap-4'}>
          <div>
            <div className="flex items-end justify-between gap-3 mb-1">
              <label className="block text-xs font-medium text-gray-700">User</label>
              {embedded && lockUser && (
                <button
                  type="button"
                  onClick={() => {
                    setLockUser(false);
                    setReporteeId('');
                    setManagerId('');
                  }}
                  className="text-xs font-bold text-indigo-700 hover:text-indigo-800"
                  title="Choose a different user"
                >
                  Change
                </button>
              )}
            </div>

            {embedded && lockUser ? (
              <div className="px-3 py-2 rounded-xl border border-gray-200 bg-gray-50">
                <div className="text-sm font-black text-gray-900 truncate">{selectedReportee?.name || 'Selected user'}</div>
                <div className="text-xs font-medium text-gray-500 truncate">{selectedReportee?.email || ''}</div>
              </div>
            ) : (
              <select
                value={reporteeId}
                onChange={(e) => setReporteeId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
              >
                <option value="">Select user...</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Manager</label>
            <select
              value={managerId}
              onChange={(e) => setManagerId(e.target.value)}
              disabled={!reporteeId}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 disabled:bg-gray-50"
            >
              <option value="">Unassigned</option>
              {managerOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} ({u.email})
                </option>
              ))}
            </select>
          </div>

          <div className={embedded ? 'flex items-center justify-end gap-2 pt-2' : 'md:col-span-2 flex justify-end'}>
            {embedded && (
              <button
                type="button"
                onClick={typeof onClose === 'function' ? onClose : undefined}
                className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !permissions.roles_and_access_user_edit}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon icon="fluent:checkmark-24-regular" width={16} height={16} />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      ))}

      {!embedded && activeTab === 'progress' && (
        <div className="rounded-[20px] border border-gray-100 bg-white shadow-sm overflow-hidden">
          <div className="p-4 md:p-5 border-b border-gray-100">
            <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-4">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">Search user</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <Icon icon="fluent:search-24-regular" width={18} />
                  </div>
                  <input
                    value={summaryQuery}
                    onChange={(e) => setSummaryQuery(e.target.value)}
                    placeholder="Search by name or email"
                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {summaryLoading ? (
            <div className="py-16 flex items-center justify-center">
              <Loader />
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="min-w-[980px] w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-5 py-3 text-xs font-black text-gray-700">User</th>
                    <th className="text-left px-5 py-3 text-xs font-black text-gray-700">Enrolled</th>
                    <th className="text-left px-5 py-3 text-xs font-black text-gray-700">Completed</th>
                    <th className="text-left px-5 py-3 text-xs font-black text-gray-700">In Progress</th>
                    <th className="text-left px-5 py-3 text-xs font-black text-gray-700">Not Started</th>
                    <th className="text-left px-5 py-3 text-xs font-black text-gray-700">Completion</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSummaryRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-5 py-10 text-center text-sm font-medium text-gray-500">
                        No results
                      </td>
                    </tr>
                  ) : (
                    filteredSummaryRows.map((r, idx) => {
                      const id = r?.user_id ?? r?.id ?? idx;
                      const name = r?.user_name ?? r?.name ?? '';
                      const email = r?.user_email ?? r?.email ?? '';
                      const enrolled = r?.enrolled_count ?? 0;
                      const completed = r?.completed_count ?? 0;
                      const inProgress = r?.in_progress_count ?? 0;
                      const notStarted = r?.not_started_count ?? 0;
                      const completion = r?.completion_percent;
                      return (
                        <tr
                          key={id}
                          className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer"
                          onClick={() => openReporteeCourses(r)}
                        >
                          <td className="px-5 py-3">
                            <div className="font-black text-gray-900 truncate max-w-[360px]">{name || 'User'}</div>
                            <div className="text-xs font-medium text-gray-500 truncate max-w-[360px]">{email}</div>
                          </td>
                          <td className="px-5 py-3"><span className="text-xs font-black text-gray-800">{Number(enrolled) || 0}</span></td>
                          <td className="px-5 py-3"><span className="text-xs font-black text-gray-800">{Number(completed) || 0}</span></td>
                          <td className="px-5 py-3"><span className="text-xs font-black text-gray-800">{Number(inProgress) || 0}</span></td>
                          <td className="px-5 py-3"><span className="text-xs font-black text-gray-800">{Number(notStarted) || 0}</span></td>
                          <td className="px-5 py-3">
                            {completion == null ? (
                              <span className="text-xs font-bold text-gray-500">-</span>
                            ) : (
                              <span className="text-xs font-black text-gray-800">{Number(completion)}%</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {courseModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setCourseModalOpen(false)} />
          <div className="relative w-full max-w-3xl bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-black text-gray-900">{courseModalUser?.user_name || 'User'} — Courses</div>
                <div className="text-xs font-medium text-gray-500 mt-0.5">{courseModalUser?.user_email || ''}</div>
              </div>
              <button
                type="button"
                onClick={() => setCourseModalOpen(false)}
                className="p-2 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200"
                aria-label="Close"
              >
                <Icon icon="fluent:dismiss-24-regular" width={18} />
              </button>
            </div>

            {courseModalLoading ? (
              <div className="py-14 flex items-center justify-center">
                <Loader />
              </div>
            ) : (
              <div className="overflow-auto max-h-[70vh]">
                <table className="min-w-[720px] w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-5 py-3 text-xs font-black text-gray-700">Course</th>
                      <th className="text-left px-5 py-3 text-xs font-black text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseModalCourses.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="px-5 py-10 text-center text-sm font-medium text-gray-500">
                          No results
                        </td>
                      </tr>
                    ) : (
                      courseModalCourses.map((c, idx) => {
                        const cid = c?.course_id ?? c?.id ?? idx;
                        const cname = c?.course_name ?? c?.name ?? c?.title ?? '';
                        return (
                          <tr key={cid} className="border-b border-gray-50 hover:bg-gray-50/50">
                            <td className="px-5 py-3">
                              <div className="font-black text-gray-900 truncate max-w-[520px]">{cname || 'Course'}</div>
                            </td>
                            <td className="px-5 py-3">{renderStatus(c?.status)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Reporting;
